import React from 'react'
import { useParams, Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { inheritPrimaryColor } from '../colors'
import getContentProps from '../get-content-props'
import Spinner from '../spinner'
import { useScrollReset } from '../scroll'
import { sort } from 'ramda'

const defaultPic = `/public/defaultuser.png`

const StyledLink = styled(Link)`
  text-decoration: none; display: inline-flex; align-items: center;
`

const preloadedMembers = typeof window !== 'undefined' && window.__data__ && window.__data__.members
const preloadedPublications = typeof window !== 'undefined' && window.__data__ && window.__data__.publications

const MemberFull = styled(({ data, ...props }) => {
  useScrollReset()
  const members = data ? data.members : preloadedMembers
  const publicationsData = data ? data.publications : preloadedPublications
  const { memberSlug } = useParams()

  const member = members.find(member => member.slug === memberSlug)

  if (!member) return <div css='align-items: center; margin-top: 30px;' {...props}><Spinner /></div>

  const {
    slug,
    title: { rendered: name },
    acf: {
      job_title: jobTitle,
      picture,
      bio,
      twitter,
      linkedin,
      publications: tmpPublications,
      scopus,
      orcid,
      contact_email: email,
      google_scholar,
      homepage
    }
  } = member

  const publications = tmpPublications || []

  const filteredPublications = publications.filter(id => publicationsData.hasOwnProperty(id))

  const sortedPublications = sort((a, b) => publicationsData[b].date - publicationsData[a].date, filteredPublications)

  return (
    <div {...props}>
      <div css='display: flex; justify-content: center;'>
        <div css='flex-grow: 1; font-weight: 500; max-width: 1140px; margin: 20px; '>
          <div>
            <StyledLink to='/team'>
              <i className='material-icons' css={`border-radius: 50%; background-color: ${inheritPrimaryColor}; color: white`}>arrow_back</i>
              <div css={`font-size: 1.2em; margin-left: 10px; color: ${inheritPrimaryColor}; ${StyledLink}:hover & { text-decoration: underline }`}>Return to all team members</div>
            </StyledLink>
          </div>
        </div>
      </div>
      <PicAndBioContainer>
        <Pic src={picture || defaultPic}></Pic>
        <Bio>
          <h1 css={css`color: ${inheritPrimaryColor}; margin-bottom: 10px;`}>{name}</h1>
          <div css='color: rgb(150, 150, 150); margin-bottom: 35px;'>{jobTitle}</div>
          <div {...getContentProps(bio)} />
          <Contacts>
            {email && <Contact type='email' text={'Email'} to={`mailto:${email}`} />}
            {homepage && <Contact type='homepage' target='_blank' text={'Home'} to={homepage} />}
            {twitter && <Contact type='twitter' target='_blank' text={`Twitter`} to={`http://www.twitter.com/${twitter}`} />}
            {linkedin && <Contact type='linkedin' target='_blank' text={'LinkedIn'} to={linkedin} />}
            {orcid && <Contact type='orcid' target='_blank' text={'ORCiD'} to={orcid} />}
            {google_scholar && <Contact type='google_scholar' target='_blank' text={'Google Scholar'} to={google_scholar} />}
            {scopus && <Contact type='scopus' target='_blank' text={'Scopus'} to={scopus} />}
          </Contacts>
          {sortedPublications.length > 0 && <Publications publicationIds={sortedPublications} publications={publicationsData} />}
        </Bio>
      </PicAndBioContainer>
    </div>
  )
})`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const Publications = styled(({ publicationIds, publications, ...props }) => {
  return (
    <div {...props}>
      <h2 css={css`margin-top: 40px; color: ${inheritPrimaryColor}`}>Published Works</h2>
      <ul css='padding-left: 25px;'>{publicationIds.map(id => id && <Publication publication={publications[id]} key={id} />)}</ul>
    </div>
  )
})``

const Publication = styled(({ publication, ...props }) => {
  return <li {...props} {...getContentProps(publication.render)}/>
})`
  line-height: 19px;
  font-size: 16px;
  margin-bottom: 20px;
`

const Contacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 40px;
`
const Bio = styled.div`
  flex-grow: 1;
  max-width: 800px;
  padding: 20px;
  font-size: 1.15em;
  line-height: 1.7em;
  color: rgb(100, 100, 100);
`
const Contact = styled(({ type, text, to, ...props }) => {
  return (
    <a href={to} {...props}>
      {type === 'email' && <span className="material-icons" css='font-size: 1.6em'>mail</span>}
      {type === 'twitter' && <img src={`/public/tw.png`} css='width: 1.5em; height: 1.5em; border-radius: 50%;' />}
      {type === 'linkedin' && <img src={`/public/linkedin.svg`} css='width: 1.5em; height: 1.5em;' />}
      {type === 'orcid' && <img src='/public/orcid.png' css='width: 1.5em; height: 1.5em' />}
      {type === 'google_scholar' && <img src='/public/google_scholar.png' css='width: 1.5em; height: 1.5em;' />}
      {type === 'scopus' && <img src='/public/scopus.png' css='width: 1.5em; height: 1.5em;' />}
      {type === 'homepage' && <span className="material-icons" css='font-size: 1.6em'>home</span>}
      <div css='margin-left: 5px; color: rgb(100, 100, 100); line-height: 20px;'>{text}</div>
    </a>
  )
})`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: darkgrey;
  font-size: 16px;
  margin-right: 20px;
  margin-top: 12px;
  &:hover div {
    text-decoration: underline
  }
`
const Pic = styled.div`
  width: 300px;
  height: 300px;
  background-image: url('${p => p.src}');
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  border: 8px solid ${inheritPrimaryColor};
  border-radius: 50%;
  margin: 30px;
`

const PicAndBioContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 35px 0px 75px;
  justify-content: center;
`

export default MemberFull